import type {
  RunFeedPreviewColumn,
  RunFeedPreviewResponse,
  RunFeedPreviewRoles,
  RunFeedPreviewRow,
  RunItemVerdict,
} from "@nova-ai/shared"

// One storefront product per config SKU — the shopper-facing view of a listing.
// `title` is brand-stripped (the QA target); `missingTitle` flags configs whose
// title column never got generated so they pop out at a glance.
export type StorefrontProduct = {
  configSku: string
  firstSimpleSku: string
  title: string
  imageUrl: string
  simpleCount: number
  missingTitle: boolean
  /** True when ANY variant of this config needs a marketplace offer (per the
   *  live offer snapshot). Drives the card's "needs offer" badge. */
  needsOffer: boolean
  /** Canonical inspection verdict; null when current evidence is unavailable. */
  verdict: RunItemVerdict | null
  /** Parked out of this run on purpose, with the operator's reason. A parked
   *  config keeps its verdict and its card, so without this flag a deliberate
   *  park reads as an OK product whose copy went missing. */
  setAside: boolean
  setAsideReason: string | null
  /** Assigned marketplace category label (code resolved through the category
   *  column's value-labels), null when the feed has no category for this config. */
  marketplaceCategory: string | null
  /** Magento (source) category leaf, e.g. "Felpe"; null when the config isn't in
   *  the catalog mirror. */
  magentoCategoryLeaf: string | null
}

/** Collapse the flat per-simple feed into one storefront product per config
 *  (title + image are config-level, so the first row of each config suffices),
 *  resolving the title/image/brand columns from `roles` and stripping the brand
 *  prefix off the title — the part that's deterministic and just adds noise when
 *  you're eyeballing the generated copy. */
export function buildStorefrontProducts(preview: RunFeedPreviewResponse): StorefrontProduct[] {
  const { roles, columns } = preview
  const brandColumn = roles.brand ? columns.find((c) => c.code === roles.brand) : undefined
  const categoryColumn = roles.category ? columns.find((c) => c.code === roles.category) : undefined

  const stateByConfig = indexVerdictItems(preview)
  const { countByConfig, needsOfferByConfig } = indexRowAggregates(preview)

  const seen = new Set<string>()
  const products: StorefrontProduct[] = []
  for (const row of preview.rows) {
    if (seen.has(row.configSku)) {
      continue
    }
    seen.add(row.configSku)
    const state = stateByConfig.get(row.configSku) ?? UNCLASSIFIED
    products.push(
      toStorefrontProduct(row, {
        roles,
        brandColumn,
        categoryColumn,
        simpleCount: row.isPlaceholder ? 0 : (countByConfig.get(row.configSku) ?? 1),
        needsOffer: needsOfferByConfig.get(row.configSku) ?? false,
        verdict: state.verdict,
        setAside: state.setAside,
        setAsideReason: state.setAsideReason,
        magentoCategoryLeaf: preview.magentoCategoryByConfigSku[row.configSku] ?? null,
      })
    )
  }
  return products
}

/** The per-config run state a card renders beside the copy: the persisted verdict
 *  and whether the config was parked out of the run (with why). */
type ConfigRunState = {
  verdict: RunItemVerdict | null
  setAside: boolean
  setAsideReason: string | null
}

/** A row whose config has no verdict item: active and not yet classified. */
const UNCLASSIFIED: ConfigRunState = { verdict: null, setAside: false, setAsideReason: null }

function indexVerdictItems(preview: RunFeedPreviewResponse): Map<string, ConfigRunState> {
  return new Map(
    preview.verdictItems.map((item) => [
      item.configSku,
      {
        verdict: preview.freshness.kind === "current" ? item.verdict : null,
        setAside: item.setAside,
        setAsideReason: item.setAsideReason,
      },
    ])
  )
}

/** Per-config aggregates that only the flat row list can answer: how many feed
 *  rows the config has, and whether ANY of them still needs a marketplace offer. */
function indexRowAggregates(preview: RunFeedPreviewResponse): {
  countByConfig: Map<string, number>
  needsOfferByConfig: Map<string, boolean>
} {
  const countByConfig = new Map<string, number>()
  const needsOfferByConfig = new Map<string, boolean>()
  for (const row of preview.rows) {
    countByConfig.set(row.configSku, (countByConfig.get(row.configSku) ?? 0) + 1)
    needsOfferByConfig.set(
      row.configSku,
      (needsOfferByConfig.get(row.configSku) ?? false) || row.needsOffer
    )
  }
  return { countByConfig, needsOfferByConfig }
}

type StorefrontRowContext = {
  roles: RunFeedPreviewRoles
  brandColumn: RunFeedPreviewColumn | undefined
  categoryColumn: RunFeedPreviewColumn | undefined
  simpleCount: number
  needsOffer: boolean
  verdict: RunItemVerdict | null
  setAside: boolean
  setAsideReason: string | null
  magentoCategoryLeaf: string | null
}

/** Build one storefront product from a config's first feed row, resolving the
 *  title/brand/category cells through their columns. The per-config aggregates
 *  (variant count, needs-offer, Magento leaf) are precomputed by the caller. */
function toStorefrontProduct(row: RunFeedPreviewRow, ctx: StorefrontRowContext): StorefrontProduct {
  const { roles, brandColumn, categoryColumn } = ctx
  const rawTitle = roles.title ? (row.values[roles.title] ?? "").trim() : ""
  const brandLabel = resolveBrandLabel(row, roles, brandColumn)
  return {
    configSku: row.configSku,
    firstSimpleSku: row.simpleSku,
    title: stripBrandPrefix(rawTitle, brandLabel),
    imageUrl: roles.image ? (row.values[roles.image] ?? "").trim() : "",
    simpleCount: ctx.simpleCount,
    missingTitle: rawTitle.length === 0,
    needsOffer: ctx.needsOffer,
    verdict: ctx.verdict,
    setAside: ctx.setAside,
    setAsideReason: ctx.setAsideReason,
    marketplaceCategory: resolveCategoryLabel(row, roles, categoryColumn),
    magentoCategoryLeaf: ctx.magentoCategoryLeaf,
  }
}

/** The human brand label for a row, resolving the raw cell code through the
 *  brand column's value-label map (e.g. `5912` → `GYMBEAM`) so we strip the
 *  brand as it actually reads in the title. */
function resolveBrandLabel(
  row: RunFeedPreviewRow,
  roles: RunFeedPreviewRoles,
  brandColumn: RunFeedPreviewColumn | undefined
): string {
  if (!roles.brand) {
    return ""
  }
  const raw = (row.values[roles.brand] ?? "").trim()
  return brandColumn?.valueLabels?.[raw] ?? raw
}

/** The human marketplace-category label for a config's row: the raw category
 *  cell resolved through the category column's value-label map (a hierarchy
 *  code → localized name). Null when the feed has no category cell for the row. */
function resolveCategoryLabel(
  row: RunFeedPreviewRow,
  roles: RunFeedPreviewRoles,
  categoryColumn: RunFeedPreviewColumn | undefined
): string | null {
  if (!roles.category) {
    return null
  }
  const raw = (row.values[roles.category] ?? "").trim()
  if (!raw) {
    return null
  }
  return categoryColumn?.valueLabels?.[raw] ?? raw
}

/** Drop a leading brand token (case-insensitive) plus any trailing separator,
 *  so "GymBeam True Whey" → "True Whey". No-ops when the title doesn't start
 *  with the brand, or when stripping would empty the title. */
export function stripBrandPrefix(title: string, brand: string): string {
  if (!brand) {
    return title
  }
  if (!title.toLowerCase().startsWith(brand.toLowerCase())) {
    return title
  }
  const stripped = title
    .slice(brand.length)
    .replace(/^[\s,\-|–—:]+/, "")
    .trim()
  return stripped.length > 0 ? stripped : title
}
